import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../core/DataTable.jsx";
import { Link } from "react-router-dom";

import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import Badge from "../../core/Badge.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";
import FormFieldProjects from "../../input/FormFieldProjects.jsx";
import api from "../../../services/api.js";
import {
  successfulToast,
  errorToast,
} from "../../../util/toastNotifications.jsx";

const ContractListPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [contractsData, setContractsData] = useState([]);
  const history = useHistory();
  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);
    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);
  const [loading, setLoading] = useState(true);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams("");
    params.set("sort", sortParams.sortBy);
    params.set("sort_dir", sortParams.sortDir);
    if (searchDebounce) {
      params.set("q", searchDebounce);
      return `/contracts/search?${params.toString()}`;
    }

    return `/contracts?${params.toString()}`;
  }, [sortParams, searchDebounce]);

  const { fetchData, data, isLoading } = useTriggeredAPICall();
  const { currentData, recordsData, listPage } = usePagination(
    data,
    endpoint,
    fetchData
  );

  const showLoading = useDelayedLoading(isLoading);

  const handleProjectRemove = async (contractId, projectId) => {
    try {
      await api.project.updateProject(projectId, {
        contract_id: null,
      });

      setContractsData((prevData) =>
        prevData.map((contract) => {
          if (contract.contract_id === contractId) {
            return {
              ...contract,
              projects: (contract.projects || []).filter(
                (p) => p.project_id !== projectId
              ),
            };
          }
          return contract;
        })
      );

      successfulToast("Project removed from contract");
    } catch (error) {
      console.error("Error removing project from contract:", error);
      errorToast("Failed to remove project from contract");
    }
  };

  const handleAddProjectToContract = (
    contractId,
    newProject,
    previousContractId
  ) => {
    setContractsData((prevData) =>
      prevData.map((contract) => {
        if (previousContractId && contract.contract_id === previousContractId) {
          return {
            ...contract,
            projects: (contract.projects || []).filter(
              (p) => p.project_id !== newProject.project_id
            ),
          };
        }

        if (contract.contract_id === contractId) {
          const existingProjects = contract.projects || [];
          if (
            existingProjects.some((p) => p.project_id === newProject.project_id)
          ) {
            return contract;
          }
          return {
            ...contract,
            projects: [...existingProjects, newProject],
          };
        }
        return contract;
      })
    );
    successfulToast("Project added to contract");
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <Link
          className="table-link truncate"
          to={{ pathname: `/contract/${row.contract_id}` }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      name: "PO #",
      id: "po_number",
      selector: (row) => row.po_number || "-",
      sortable: true,
      width: "95px",
    },
    {
      name: "Contract #",
      id: "contract_number",
      selector: (row) => row.contract_number || "-",
      sortable: true,
      width: "95px",
    },
    {
      name: "Company",
      id: "client",
      selector: (row) => row.company?.name || "-",
      sortable: true,
      width: "130px",
    },
    {
      name: "Start Date",
      selector: (row) => row.start_date,
      sortable: true,
      width: "85px",
      cell: (row) => (
        <span>
          {row.start_date ? new Date(row.start_date).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      name: "End Date",
      selector: (row) => row.end_date,
      sortable: true,
      width: "85px",
      cell: (row) => (
        <span>
          {row.end_date ? new Date(row.end_date).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      id: "is_active",
      name: "Active",
      selector: (row) => row.is_active,
      sortable: true,
      width: "70px",
      cell: (row) => (
        <Badge status={row.is_active ? "Active" : "Inactive"} type="status" />
      ),
    },
    {
      id: "projects",
      name: "Projects",
      selector: (row) => row.projects?.map((p) => p.name).join(", ") || "-",
      sortable: false,
      width: "130px",
      cell: (row) => (
        <FormFieldProjects
          onClick={(e) => e.stopPropagation()}
          hideLabel
          selectedProjects={row.projects || []}
          contractId={row.contract_id}
          handleProjectRemove={(projectId) =>
            handleProjectRemove(row.contract_id, projectId)
          }
          handleAddProjectToContract={(newProject, previousContractId) =>
            handleAddProjectToContract(
              row.contract_id,
              newProject,
              previousContractId
            )
          }
        />
      ),
    },
  ];

  useEffect(() => {
    listPage(1);
  }, [searchDebounce, endpoint]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  useEffect(() => {
    if (data?.results) {
      setContractsData(data.results);
    }
  }, [data]);

  if (showLoading && !currentData) {
    return <Loading />;
  }

  if (!currentData) {
    return null;
  }

  const displayData = currentData.map((contract) => {
    const localContract = contractsData.find(
      (c) => c.contract_id === contract.contract_id
    );
    return localContract || contract;
  });

  return (
    <div className="flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faFileContract} className="contracts-icon " />{" "}
        Contracts
      </h1>
      <div className="flex-space-between">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="width-200px"
        />
        <Button to="/contract-form">+ New Contract</Button>
      </div>

      <div className="contract-list-data-table">
        <DataTable
          columns={columns}
          data={currentData}
          loading={showLoading}
          onRowClick={(row) => history.push(`/contract/${row.contract_id}`)}
          pointerOnHover
          highlightOnHover
          recordsData={recordsData}
          onPageChange={listPage}
          baseUrl={endpoint}
          setSortParams={setSortParams}
          sortParams={sortParams}
          defaultSortFieldId={sortParams?.sortBy}
          defaultSortAsc={sortParams?.sortDir !== "desc"}
          isSortedManually={true}
        />
      </div>
    </div>
  );
};

export default ContractListPage;
